import {
	Avatar,
	Button,
	Flex,
	FormControl,
	FormLabel,
	Icon,
	Menu,
	MenuButton,
	MenuDivider,
	MenuItem,
	MenuList,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
	useColorModeValue,
	useDisclosure
} from '@chakra-ui/react';
import { useState } from 'react';
import { MdLock, MdOutlineAccountBalanceWallet, MdVpnKey } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import PasswordInput from '../passwordInput/PasswordInput';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { RootState } from '../../store/store';
import { changeMyPassword } from '../../store/features/user/user.slice';
import { datricApi } from '../../services/api/datric.api';
import { toastService } from '../../services/toast.service';
import { formatBRL, formatQueries } from '../../utils/format';

export default function NavbarLinksAdmin() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { displayName, balance, pricePerQuery } = useAppSelector((state: RootState) => state.auth);
	const menuBg = useColorModeValue('white', 'navy.800');
	const textColor = useColorModeValue('secondaryGray.900', 'white');

	const passwordModal = useDisclosure();
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');

	const openPasswordModal = () => {
		setCurrentPassword('');
		setNewPassword('');
		passwordModal.onOpen();
	};

	const handleChangePassword = async () => {
		if (!currentPassword) {
			toastService.warning('Informe a senha atual');
			return;
		}
		if (newPassword.length < 6) {
			toastService.warning('A nova senha deve ter ao menos 6 caracteres');
			return;
		}
		const result = await dispatch(changeMyPassword({ currentPassword, newPassword }));
		// Só fecha se o servidor aceitou: senha atual errada mantém o modal aberto.
		if (changeMyPassword.fulfilled.match(result)) {
			passwordModal.onClose();
		}
	};

	const initials = (displayName || 'U')
		.split(' ')
		.map(n => n[0])
		.slice(0, 2)
		.join('')
		.toUpperCase();

	const goToMyApiKeys = () => navigate('/admin/minhas-api-keys');

	return (
		<Flex
			w={{ sm: '100%', md: 'auto' }}
			alignItems='center'
			flexDirection='row'
			bg={menuBg}
			p='10px'
			borderRadius='30px'
			boxShadow='14px 17px 40px 4px rgba(112, 144, 176, 0.18)'>
			<Flex
				align='center'
				bg='brand.50'
				borderRadius='20px'
				px='14px'
				py='6px'
				me='12px'
				cursor='pointer'
				_hover={{ bg: 'brand.100' }}
				transition='.2s all ease'
				onClick={goToMyApiKeys}
				display={{ base: 'none', md: 'flex' }}>
				<Icon as={MdOutlineAccountBalanceWallet} color='brand.500' me='6px' w='18px' h='18px' />
				<Text color='brand.600' fontSize='sm' fontWeight='700'>
					{formatBRL(balance)}
				</Text>
				{/* Quantas consultas o saldo ainda dá, no preço do próprio usuário. */}
				<Text color='brand.500' fontSize='sm' fontWeight='500' ms='6px'>
					· {formatQueries(balance, pricePerQuery)} consultas
				</Text>
			</Flex>
			<Menu>
				<MenuButton p='0px'>
					<Avatar
						_hover={{ cursor: 'pointer' }}
						color='white'
						name={initials}
						bg='brand.500'
						size='sm'
						w='40px'
						h='40px'
					/>
				</MenuButton>
				<MenuList boxShadow='lg' p='0px' mt='10px' borderRadius='20px' bg={menuBg} border='none'>
					<Flex w='100%' mb='0px'>
						<Text
							ps='20px'
							pt='16px'
							pb='10px'
							w='100%'
							borderBottom='1px solid'
							borderColor='secondaryGray.200'
							fontSize='sm'
							fontWeight='700'
							color={textColor}>
							Olá, {displayName || 'Usuário'}
						</Text>
					</Flex>
					<Flex flexDirection='column' p='10px'>
						<MenuItem
							_hover={{ bg: 'none' }}
							borderRadius='8px'
							px='14px'
							fontWeight='600'
							color={textColor}
							icon={<Icon as={MdVpnKey} w='16px' h='16px' />}
							onClick={goToMyApiKeys}>
							Minhas API Keys
						</MenuItem>
						<MenuItem
							_hover={{ bg: 'none' }}
							borderRadius='8px'
							px='14px'
							fontWeight='600'
							color={textColor}
							icon={<Icon as={MdLock} w='16px' h='16px' />}
							onClick={openPasswordModal}>
							Trocar senha
						</MenuItem>
						<MenuDivider />
						<MenuItem
							_hover={{ bg: 'none' }}
							borderRadius='8px'
							px='14px'
							color='red.500'
							fontWeight='600'
							onClick={() => datricApi.logout()}>
							Sair
						</MenuItem>
					</Flex>
				</MenuList>
			</Menu>

			<Modal isOpen={passwordModal.isOpen} onClose={passwordModal.onClose} isCentered>
				<ModalOverlay />
				<ModalContent borderRadius='20px'>
					<ModalHeader>Trocar senha</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<FormControl mb='12px'>
							<FormLabel fontSize='sm'>Senha atual</FormLabel>
							<PasswordInput value={currentPassword} onChange={setCurrentPassword} withGenerator={false} />
						</FormControl>
						<FormControl>
							<FormLabel fontSize='sm'>Nova senha</FormLabel>
							<PasswordInput value={newPassword} onChange={setNewPassword} placeholder='Mínimo 6 caracteres' />
						</FormControl>
					</ModalBody>
					<ModalFooter gap='10px'>
						<Button variant='light' onClick={passwordModal.onClose}>
							Cancelar
						</Button>
						<Button variant='brand' onClick={handleChangePassword}>
							Salvar
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</Flex>
	);
}
